import { formatMinor, remainingMinor } from '@didto/domain';
import { Badge, Button, EmptyState, Field, Input, Notice, Sheet } from '@didto/ui';
import type { Tone } from '@didto/ui';
import { useState } from 'react';
import { useOnline } from '@/lib/network';
import type { Order } from '@/lib/orders';
import { PAYMENT_STATUS_TONE, fulfilmentTone } from '@/lib/status';
import type { TableSession } from '@/lib/tableSessions';
import { StateBadge } from '../kds/live';
import type { ClearTarget, ForceClearRequest } from './tableSupport';
import './tables.css';

export interface OccupancyTable {
  id: string;
  name: string;
  seats: number | null;
}

const clockTime = (iso: string, timeZone: string) =>
  new Intl.DateTimeFormat('en-PH', { timeZone, hour: 'numeric', minute: '2-digit' }).format(
    new Date(iso),
  );

/**
 * A table's occupancy (ADR-021) in a word, a tone and a shape: a hollow ring is free, a filled
 * square is taken. Occupancy is the table's open session, whatever its orders did.
 */
export function TableState({ occupied }: { occupied: boolean }) {
  return occupied ? (
    <StateBadge tone="warning" mark="square">
      occupied
    </StateBadge>
  ) : (
    <StateBadge tone="success" mark="ring">
      available
    </StateBadge>
  );
}

/**
 * Tables with their occupancy (ADR-021): a table is occupied while it has an open session, whatever
 * its orders did. Shared by the POS Tables sheet, the Expo Tables drawer and the Tables page.
 * Clearing a table needs the server now: it waits for the connection (ADR-037).
 */
export function TableOccupancyList({
  tables,
  sessions,
  orders,
  currency,
  timeZone,
  canClear,
  busy,
  onClear,
  onNewOrder,
}: {
  tables: OccupancyTable[];
  sessions: TableSession[];
  /** Open orders of the location; counted per session and summed for TO SETTLE. */
  orders: Order[];
  currency: string;
  timeZone: string;
  canClear: boolean;
  busy: string | null;
  onClear: (table: ClearTarget) => void;
  onNewOrder?: (table: OccupancyTable) => void;
}) {
  const online = useOnline();
  const sessionOf = (tableId: string) => sessions.find((s) => s.dining_table_id === tableId);
  return (
    <div className="d-list" data-testid="table-occupancy">
      {tables.length === 0 && (
        <EmptyState title="No tables yet">Create them under Tables.</EmptyState>
      )}
      {tables.map((t) => {
        const s = sessionOf(t.id);
        const open = s ? orders.filter((o) => o.table_session_id === s.id) : [];
        const owed = open.reduce(
          (a, o) =>
            a +
            remainingMinor({
              totalMinor: o.total_minor,
              paidMinor: o.paid_minor,
              refundedMinor: o.refunded_minor,
              writtenOffMinor: o.written_off_minor,
            }),
          0,
        );
        const meta = s
          ? [
              `since ${clockTime(s.opened_at, timeZone)}`,
              s.covers ? `${s.covers} guest${s.covers === 1 ? '' : 's'}` : null,
              `${open.length} open order${open.length === 1 ? '' : 's'}`,
            ]
              .filter(Boolean)
              .join(' · ')
          : t.seats
            ? `${t.seats} seats`
            : '';
        const actions = !!onNewOrder || (!!s && canClear);
        return (
          <div
            key={t.id}
            className={`d-row tables-row${s ? ' tables-row--occupied' : ''}`}
            data-testid={`occupancy-${t.name}`}
            data-status={s ? 'occupied' : 'available'}
          >
            <div className="d-row__main">
              <div className="d-row__title tables-row__title">
                <span className="tables-row__name">{t.name}</span>
                <TableState occupied={!!s} />
              </div>
              {(meta || owed > 0) && (
                <div className="d-row__meta tables-row__meta">
                  {meta && <span>{meta}</span>}
                  {owed > 0 && (
                    <StateBadge tone={PAYMENT_STATUS_TONE.unpaid} mark="alert">
                      <span className="d-mono">{formatMinor(owed, currency)}</span> to settle
                    </StateBadge>
                  )}
                </div>
              )}
            </div>
            {actions && (
              <div className="d-actions">
                {onNewOrder && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onNewOrder(t)}
                    data-testid={`new-order-${t.name}`}
                  >
                    New order
                  </Button>
                )}
                {s && canClear && (
                  <Button
                    size="sm"
                    variant="danger"
                    busy={busy === t.id}
                    disabled={!online}
                    onClick={() => onClear({ id: t.id, name: t.name })}
                    data-testid={`clear-${t.name}`}
                  >
                    Clear table
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** The server lists the open orders with their states as plain text (ADR-022). */
const paymentTone = (status: string): Tone =>
  (PAYMENT_STATUS_TONE as Partial<Record<string, Tone>>)[status] ?? 'neutral';
const fulfilTone = (status: string): Tone =>
  fulfilmentTone(status as Parameters<typeof fulfilmentTone>[0]);

/**
 * Force clear (ADR-022): the table still has open orders and the employee holds table.force_clear
 * (the cashier or the owner). A reason is required; the orders stay open with their history.
 */
export function ForceClearSheet({
  request,
  busy,
  onConfirm,
  onClose,
}: {
  request: ForceClearRequest | null;
  busy: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  return (
    <Sheet
      open={!!request}
      onClose={onClose}
      title={request ? `Force clear ${request.table.name}` : 'Force clear'}
    >
      {/* The body unmounts with the sheet, so every force clear starts with an empty reason. */}
      {request && (
        <ForceClearBody request={request} busy={busy} onConfirm={onConfirm} onClose={onClose} />
      )}
    </Sheet>
  );
}

function ForceClearBody({
  request,
  busy,
  onConfirm,
  onClose,
}: {
  request: ForceClearRequest;
  busy: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const online = useOnline();
  const [reason, setReason] = useState('');
  const n = request.openOrders.length;
  return (
    <div className="d-stack" data-testid="force-clear-sheet">
      <Notice tone="warning">
        {n} order{n === 1 ? '' : 's'} at {request.table.name} {n === 1 ? 'is' : 'are'} still open.
        Force clearing frees the table now; the orders stay in the open list until they are settled
        or voided.
      </Notice>
      <div className="d-list">
        {request.openOrders.map((o) => (
          <div className="d-row" key={o.order_id}>
            <div className="d-row__main">
              <div className="d-row__title d-mono">{o.order_number}</div>
            </div>
            <div className="d-actions">
              <Badge tone={paymentTone(o.payment_status)}>
                {o.payment_status.replace('_', ' ')}
              </Badge>
              <Badge tone={fulfilTone(o.fulfilment_status)}>
                {o.fulfilment_status.replace('_', ' ')}
              </Badge>
            </div>
          </div>
        ))}
      </div>
      <Field label="Reason" help="Required; it is kept in the audit log.">
        {(id) => (
          <Input
            id={id}
            value={reason}
            maxLength={200}
            onChange={(e) => setReason(e.target.value)}
            data-testid="force-clear-reason"
          />
        )}
      </Field>
      <div className="d-sheet__actions">
        <Button variant="quiet" onClick={onClose}>
          Back
        </Button>
        <Button
          variant="danger-solid"
          busy={busy}
          disabled={!online || !reason.trim()}
          onClick={() => onConfirm(reason.trim())}
          data-testid="force-clear-confirm"
        >
          Force clear
        </Button>
      </div>
    </div>
  );
}
